import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Switch } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BomClient, formatNumber, revisionLabel, type BomWhereUsedDTO, type PartDTO } from "../../api/manufacturing";
import { OpenRecord } from "../../components/OpenRecord";
import { bomStatusLabel } from "./PartBomTab";

/**
 * What this part goes into.
 *
 * <p>SIGM could answer this only by scanning its 93,788 bill-of-materials rows,
 * so in practice nobody asked — and it is the question that comes up every time
 * a component is discontinued or a supplier changes a specification.</p>
 *
 * <p>One level, not a full reverse explosion. "What contains this directly" is
 * what a person is deciding about; "what eventually contains this" is answered
 * by following one of these rows up.</p>
 */
export function PartWhereUsedTab({ part }: { part: PartDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [includeInactive, setIncludeInactive] = useState(false);

  const boms = useMemo(() => new BomClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["manufacturing", "bom", "where-used", part.id, includeInactive],
    queryFn: () => boms.whereUsed(part.id, includeInactive),
    staleTime: 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<BomWhereUsedDTO>();
    return [
      c.text("parent", (row) => row.parent_part_number, { headerKey: "column.bom.used-in", priority: 1 }),
      c.text("parent_name", (row) => row.parent_part_name, { headerKey: "column.part.name", priority: 2 }),
      c.text("quantity", (row) => formatNumber(row.quantity), { headerKey: "column.bom.quantity", priority: 3 }),
      c.text("unit", (row) => row.unit_code ?? "", { headerKey: "column.bom.unit" }),
      c.text("revision", (row) => revisionLabel(row.revision_at, row.revision), { headerKey: "column.bom.revision", defaultVisible: false, filter: "none" }),
      c.custom(
        "status",
        // The label, not the enum: this table filters in the browser and the
        // filter lists what the accessor returns.
        (row) => bomStatusLabel(row.status, t),
        (row) => (
          <Badge
            variant={row.status === "ACTIVE" ? "success" : row.status === "DRAFT" ? "warning" : "neutral"}
            size="sm"
          >
            {bomStatusLabel(row.status, t)}
          </Badge>
        ),
        { headerKey: "column.status", filter: "select" },
      ),
      c.number("position", (row) => row.position, { headerKey: "column.bom.position", defaultVisible: false }),
    ];
  }, [t]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2 lg:px-6">
        <Switch
          checked={includeInactive}
          onChange={setIncludeInactive}
          label={t("bom.where-used.include-inactive", "Include draft and archived revisions")}
        />
        {data && (
          <Badge variant="neutral" size="sm">
            {t("bom.where-used.count", "{{n}} parents", { n: data.length })}
          </Badge>
        )}
      </div>
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.bom_id) + ":" + String(row.position)}
        loading={isLoading}
        rowActions={(row) => (
          <OpenRecord target={{ kind: "part", id: row.parent_part_id, tab: "bom" }} label={row.parent_part_number ?? ""} />
        )}
        emptyMessage={t("bom.where-used.empty", "This part is not used in any bill of materials.")}
      />
    </div>
  );
}
